import {
  Alert,
  Button,
  Form,
  Input,
  message,
  Modal,
  Radio,
} from "antd";
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useState } from "react";
import NetworkApi from "../../network/networkApi";
import AddressSelect from "../lib/AddressSelect";

type RegistrationType = "organization" | "farmer";

interface Props {
  visible: boolean;
  hide: () => void;
}

const citizenMismatchMessage =
  "Улсын бүртгэлийн ерөнхий газрын мэдээллээс зөрж байна. Мэдээллээ шалгана уу.";
const passwordPolicyMessage =
  "Нууц үг нь 8-50 тэмдэгт, том үсэг, жижиг үсэг, тоо, тусгай тэмдэгт агуулсан байна.";
const registerNumberPattern = /^[А-ЯӨҮЁ]{2}[0-9]{8}$/i;
const cyrillicNamePattern = /^[а-яүөёА-ЯҮӨЁ -]{2,56}$/;
const phonePattern = /^[0-9]{6,15}$/;
const loginNamePattern = /^(?=[a-zA-Z0-9._]{6,20}$)(?!.*[_.]{2})[^_.].*[^_.]$/;
const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])\S{8,50}$/;

const initialValues = {
  rd: "",
  userRd: "",
  organizationName: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  aid: "",
  name: "",
  password: "",
  confirmPassword: "",
  aimagId: undefined,
  sumId: undefined,
  bagId: undefined,
};

const Register = ({ visible, hide }: Props) => {
  const [form] = Form.useForm();
  const [registrationType, setRegistrationType] =
    useState<RegistrationType>("organization");
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationResolved, setOrganizationResolved] = useState(false);
  const [organizationError, setOrganizationError] = useState("");
  const [organizationInfo, setOrganizationInfo] = useState<{
    rd: string;
    name: string;
  } | null>(null);
  const [citizenCheckLoading, setCitizenCheckLoading] = useState(false);
  const [citizenVerified, setCitizenVerified] = useState(false);
  const [citizenCheckError, setCitizenCheckError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resetState = useCallback(() => {
    setRegistrationType("organization");
    setOrganizationLoading(false);
    setOrganizationResolved(false);
    setOrganizationError("");
    setOrganizationInfo(null);
    setCitizenCheckLoading(false);
    setCitizenVerified(false);
    setCitizenCheckError("");
    setSubmitting(false);
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible, resetState]);

  const handleCancel = useCallback(() => {
    resetState();
    hide();
  }, [hide, resetState]);

  const handleTypeChange = useCallback(
    (nextType: RegistrationType) => {
      setRegistrationType(nextType);
      setOrganizationResolved(false);
      setOrganizationError("");
      setOrganizationInfo(null);
      setCitizenVerified(false);
      setCitizenCheckError("");
      form.resetFields();
    },
    [form],
  );

  const handleValuesChange = useCallback(
    (changedValues: any) => {
      if (registrationType === "farmer") {
        if (
          Object.prototype.hasOwnProperty.call(changedValues, "rd") ||
          Object.prototype.hasOwnProperty.call(changedValues, "firstName") ||
          Object.prototype.hasOwnProperty.call(changedValues, "lastName")
        ) {
          setCitizenVerified(false);
          setCitizenCheckError("");
        }
      }

      if (
        registrationType === "organization" &&
        Object.prototype.hasOwnProperty.call(changedValues, "rd")
      ) {
        setOrganizationResolved(false);
        setOrganizationError("");
        setOrganizationInfo(null);
        form.setFieldsValue({ organizationName: "" });
      }
    },
    [form, registrationType],
  );

  const validateCitizenInfo = useCallback(async () => {
    try {
      const values = await form.validateFields(["rd", "lastName", "firstName"]);
      const payload = {
        rd: String(values.rd || "").trim().toUpperCase(),
        lastName: String(values.lastName || "").trim(),
        firstName: String(values.firstName || "").trim(),
      };

      form.setFieldsValue({ rd: payload.rd });
      setCitizenCheckLoading(true);
      setCitizenCheckError("");
      setCitizenVerified(false);

      const response = await NetworkApi.requestPost(
        "/api/farmer/register/check",
        payload,
      );

      if (response.resultCode === 200) {
        setCitizenVerified(true);
        return;
      }

      setCitizenCheckError(response.message || citizenMismatchMessage);
    } catch (error) {
      return;
    } finally {
        setCitizenCheckLoading(false);
    }
  }, [form]);

  const checkOrganization = useCallback(async () => {
    try {
      const values = await form.validateFields(["rd"]);
      const rd = String(values.rd || "").trim();
      const normalizedRd = rd.toUpperCase();

      setOrganizationLoading(true);
      setOrganizationError("");

      const response = await NetworkApi.requestGet(
        `/api/organization/check/${normalizedRd}`,
      );

      if (response.resultCode === 200) {
        const organizationName = response.result.name || "";
        setOrganizationResolved(true);
        setOrganizationInfo({
          rd: normalizedRd,
          name: organizationName,
        });
        form.setFieldsValue({
          rd: normalizedRd,
          organizationName,
        });
        return;
      }

      setOrganizationResolved(false);
      setOrganizationInfo(null);
      setOrganizationError(
        response.message || "Регистрийн дугаараа шалгаад дахин оролдоно уу.",
      );
    } catch (error) {
      return;
    } finally {
      setOrganizationLoading(false);
    }
  }, [form]);

  const submitRegister = useCallback(
    async (values: any) => {
      if (registrationType === "farmer" && !citizenVerified) {
        setCitizenCheckError(citizenMismatchMessage);
        return;
      }

      if (registrationType === "organization" && !organizationResolved) {
        setOrganizationError("Байгууллагын регистрээ эхэлж шалгана уу.");
        return;
      }

      setSubmitting(true);

      const payload = {
        ...values,
        rd: values.rd?.trim()?.toUpperCase(),
        userRd: values.userRd?.trim()?.toUpperCase(),
        type: registrationType,
      };

      const url =
        registrationType === "farmer"
          ? "/api/farmer/register"
          : "/api/organization/register";

      const result = await NetworkApi.requestPost(url, payload);
      if (result.resultCode === 200) {
        window.location.reload();
        return;
      }

      setSubmitting(false);
      message.error(result.message || "system error");
    },
    [citizenVerified, organizationResolved, registrationType],
  );

  const renderPasswordFields = () => (
    <>
      <Form.Item className="register-field-full">
        <div className="register-password-hint">{passwordPolicyMessage}</div>
      </Form.Item>
      <div className="register-two-col">
        <Form.Item
          name="password"
          label="Нууц үг"
          rules={[
            { required: true, message: "Нууц үгээ оруулна уу." },
            {
              pattern: passwordPattern,
              message: passwordPolicyMessage,
            },
          ]}
          hasFeedback
        >
          <Input.Password placeholder="Нууц үг" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Нууц үгээ давтан оруулах"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Нууц үгээ давтан оруулна уу." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Нууц үгс ижил биш байна"));
              },
            }),
          ]}
          hasFeedback
        >
          <Input.Password placeholder="Нууц үгээ давтан оруулна уу" />
        </Form.Item>
      </div>
    </>
  );

  const renderFarmerFields = () => (
    <>
      <div className="register-check-card">
        <div className="register-section-title">Иргэний бүртгэл</div>
        <p className="register-section-desc">
          Регистрийн дугаар, овог, нэрээ оруулаад улсын бүртгэлийн мэдээлэлтэй
          тулгана уу.
        </p>
        <div className="register-two-col">
          <Form.Item
            name="rd"
            label="Регистрийн дугаар"
            rules={[
              {
                required: true,
                message: "Регистрийн дугаараа оруулна уу.",
              },
              {
                pattern: registerNumberPattern,
                message: "АА00000000 хэлбэртэй регистрийн дугаар оруулна уу.",
              },
            ]}
          >
            <Input
              maxLength={10}
              placeholder="АА00000000"
              onBlur={(event) => {
                form.setFieldsValue({
                  rd: event.target.value?.trim()?.toUpperCase(),
                });
              }}
            />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Овог"
            rules={[
              { required: true, message: "Овгоо оруулна уу." },
              {
                pattern: cyrillicNamePattern,
                message: "Овгийг кирилл үсгээр зөв оруулна уу.",
              },
            ]}
          >
            <Input placeholder="Овог" />
          </Form.Item>
        </div>
        <div className="register-two-col register-two-col-single">
          <Form.Item
            name="firstName"
            label="Нэр"
            rules={[
              { required: true, message: "Нэрээ оруулна уу." },
              {
                pattern: cyrillicNamePattern,
                message: "Нэрийг кирилл үсгээр зөв оруулна уу.",
              },
            ]}
          >
            <Input placeholder="Нэр" />
          </Form.Item>
          <div className="register-check-action">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={citizenCheckLoading}
              onClick={validateCitizenInfo}
            >
              ШАЛГАХ
            </Button>
          </div>
        </div>
        {citizenCheckError ? (
          <Alert
            className="register-inline-alert"
            type="error"
            showIcon
            message={citizenCheckError}
          />
        ) : null}
        {citizenVerified ? (
          <Alert
            className="register-inline-alert"
            type="success"
            showIcon
            message="Иргэний мэдээлэл баталгаажлаа. Үлдсэн мэдээллээ бөглөнө үү."
          />
        ) : null}
      </div>

      {citizenVerified ? (
        <>
          <div className="register-form-divider" />
          <div className="register-two-col">
            <Form.Item
              name="phone"
              label="Утас"
              rules={[
                { required: true, message: "Утасны дугаараа оруулна уу." },
                {
                  pattern: phonePattern,
                  message: "Утасны дугаарыг зөвхөн тоогоор оруулна уу.",
                },
              ]}
            >
              <Input placeholder="Утасны дугаар" />
            </Form.Item>
            <Form.Item
              name="email"
              label="И-мэйл хаяг"
              rules={[
                { required: true, message: "И-мэйл хаягаа оруулна уу." },
                { type: "email", message: "И-мэйл хаяг буруу байна." },
              ]}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>
          </div>
          <div className="register-two-col">
            <Form.Item
              name="name"
              label="Системд нэвтрэх нэр"
              rules={[
                {
                  required: true,
                  message: "Системд нэвтрэх нэрээ оруулна уу.",
                },
                {
                  pattern: loginNamePattern,
                  message:
                    "Нэвтрэх нэр нь латин үсэг, тоо, цэг, доогуур зураас агуулсан 6-20 тэмдэгт байна.",
                },
              ]}
            >
              <Input placeholder="Нэвтрэх нэр" />
            </Form.Item>
          </div>
          <AddressSelect form={form} />
          {renderPasswordFields()}
          <div className="register-actions">
            <Button onClick={handleCancel}>Болих</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Бүртгүүлэх
            </Button>
          </div>
        </>
      ) : null}
    </>
  );

  const renderOrganizationFields = () => (
    <>
      <div className="register-org-check">
        <div className="register-section-title">Хуулийн этгээдийн бүртгэл</div>
        <p className="register-section-desc">
          Байгууллагын регистрийн дугаарыг шалгаад үргэлжлүүлнэ үү.
        </p>
        <div className="register-two-col register-two-col-single">
          <Form.Item
            name="rd"
            label="Хуулийн этгээдийн регистр"
            rules={[
              {
                required: true,
                message: "Хуулийн этгээдийн регистрийн дугаараа оруулна уу.",
              },
              {
                pattern: /^[0-9]{7}$/,
                message: "Хуулийн этгээдийн регистр 7 оронтой тоо байна.",
              },
            ]}
          >
            <Input maxLength={7} placeholder="1234567" />
          </Form.Item>
          <div className="register-check-action">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={organizationLoading}
              onClick={checkOrganization}
            >
              ШАЛГАХ
            </Button>
          </div>
        </div>
        {organizationError ? (
          <Alert
            className="register-inline-alert"
            type="error"
            showIcon
            message={organizationError}
          />
        ) : null}
      </div>

      {organizationResolved && organizationInfo ? (
        <>
          <div className="register-org-info">
            <div className="register-org-badge">
              <ApartmentOutlined />
            </div>
            <div>
              <div className="register-org-label">Баталгаажсан байгууллага</div>
              <div className="register-org-name">{organizationInfo.name}</div>
              <div className="register-org-rd">Регистр: {organizationInfo.rd}</div>
            </div>
          </div>
          <div className="register-two-col">
            <Form.Item name="organizationName" label="Хуулийн этгээдийн нэр">
              <Input disabled />
            </Form.Item>
            <Form.Item
              name="userRd"
              label="Захирлын регистрийн дугаар"
              rules={[
                {
                  pattern: registerNumberPattern,
                  message: "АА00000000 хэлбэртэй регистрийн дугаар оруулна уу.",
                },
              ]}
            >
              <Input
                maxLength={10}
                placeholder="АА00000000"
                onBlur={(event) => {
                  form.setFieldsValue({
                    userRd: event.target.value?.trim()?.toUpperCase(),
                  });
                }}
              />
            </Form.Item>
          </div>
          <div className="register-two-col">
            <Form.Item
              name="lastName"
              label="Захирлын овог"
              rules={[
                {
                  pattern: cyrillicNamePattern,
                  message: "Овгийг кирилл үсгээр зөв оруулна уу.",
                },
              ]}
            >
              <Input placeholder="Захирлын овог" />
            </Form.Item>
            <Form.Item
              name="firstName"
              label="Захирлын нэр"
              rules={[
                {
                  pattern: cyrillicNamePattern,
                  message: "Нэрийг кирилл үсгээр зөв оруулна уу.",
                },
              ]}
            >
              <Input placeholder="Захирлын нэр" />
            </Form.Item>
          </div>
          <div className="register-two-col">
            <Form.Item
              name="phone"
              label="Утас"
              rules={[
                { required: true, message: "Утасны дугаараа оруулна уу." },
                {
                  pattern: phonePattern,
                  message: "Утасны дугаарыг зөвхөн тоогоор оруулна уу.",
                },
              ]}
            >
              <Input placeholder="Утасны дугаар" />
            </Form.Item>
            <Form.Item
              name="email"
              label="И-мэйл хаяг"
              rules={[
                { required: true, message: "И-мэйл хаягаа оруулна уу." },
                { type: "email", message: "И-мэйл хаяг буруу байна." },
              ]}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>
          </div>
          <div className="register-two-col">
            <Form.Item
              name="aid"
              label="Анхан шатны үйлдвэрлэл эрхлэгчийн дугаар"
              rules={[
                {
                  pattern: /^[0-9]{6,15}$/,
                  message: "Анхан шатны дугаарыг зөвхөн тоогоор оруулна уу.",
                },
              ]}
            >
              <Input placeholder="Анхан шатны дугаар" />
            </Form.Item>
            <Form.Item
              name="name"
              label="Системд нэвтрэх нэр"
              rules={[
                {
                  required: true,
                  message: "Системд нэвтрэх нэрээ оруулна уу.",
                },
                {
                  pattern: loginNamePattern,
                  message:
                    "Нэвтрэх нэр нь латин үсэг, тоо, цэг, доогуур зураас агуулсан 6-20 тэмдэгт байна.",
                },
              ]}
            >
              <Input placeholder="Нэвтрэх нэр" />
            </Form.Item>
          </div>
          <AddressSelect form={form} />
          {renderPasswordFields()}
          <div className="register-actions">
            <Button onClick={handleCancel}>Болих</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Бүртгүүлэх
            </Button>
          </div>
        </>
      ) : null}
    </>
  );

  return (
    <Modal
      width={820}
      centered
      visible={visible}
      footer={null}
      title={null}
      onCancel={handleCancel}
      className="register-modal"
      destroyOnClose
      maskClosable={false}
    >
      <div className="register-modal-header">
        <div className="register-modal-emblem">
          <UserOutlined />
        </div>
        <h3 className="register-modal-title">Шинэ хэрэглэгчийн бүртгэл</h3>
        <p className="register-modal-desc">
          Нүүр хуудасны загвартай нийцсэн, үе шаттай бүртгэлийн маягт
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        className="register-form"
        initialValues={initialValues}
        onFinish={submitRegister}
        onValuesChange={handleValuesChange}
      >
        <Form.Item className="register-type-select">
          <Radio.Group
            value={registrationType}
            buttonStyle="solid"
            className="register-radio-group"
            onChange={(event) =>
              handleTypeChange(event.target.value as RegistrationType)
            }
          >
            <Radio.Button value="organization">
              <ApartmentOutlined />
              Хуулийн этгээд
            </Radio.Button>
            <Radio.Button value="farmer">
              <CheckCircleOutlined />
              Иргэний бүртгэл
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {registrationType === "organization"
          ? renderOrganizationFields()
          : renderFarmerFields()}
      </Form>
    </Modal>
  );
};

export default Register;
